<template>
    <div class="ers-page-content">
        <div class="ers-page-title">
            <h1>
                {{ $t('energy.consumers.title') }}
            </h1>
        </div>

        <div class="ers-page-title">
            <h2>
                {{ $t('energy.consumers.question_1a') }}
                <Tooltip
                    class="ers-page-title-tooltip"
                    :label="$t('energy.consumers.tooltip_1')"
                />
            </h2>
        </div>

        <div class="ers-page-section">
            <people-select v-model="n_residents" :max="12" />
        </div>

        <div v-if="n_residents > 0" class="ers-page-consumption">
            <div v-if="getFeatures.energy_consumption_known" class="ers-page-section">
                <h2>
                    {{ $t('energy.consumers.question_2a') }}
                    <Tooltip
                        class="ers-page-title-tooltip"
                        :label="$t('energy.consumers.tooltip_2')"
                    />
                </h2>
                <div class="ers-page-info">
                    <img src="@/assets/icons/ic-lightbulb.svg" alt="" srcset="" />
                    <p>{{ $t('energy.consumers.bubble_info') }}</p>
                </div>
                <div class="section-inputs">
                    <b-field
                        class="section-inputs-item"
                        :label="$t(`value.${getHeatingFuel}`)"
                        :type="{
                            'is-danger': !$v.fuel.amount.numeric,
                        }"
                        :message="[
                            !$v.fuel.amount.numeric
                                ? $t('validation.only_numbers')
                                : null,
                        ]"
                    >
                        <b-input
                            v-model="fuel.amount"
                            size="is-medium"
                            class="ers-input-field"
                        />
                        <p class="unit">
                            {{ $t(`unit.${fuel.unit}/year`) }}
                        </p>
                    </b-field>

                    <div
                        v-if="
                            !getIsFuelElectricity && getHeatingFuel !== 'district_heating'
                        "
                        class="section-radios"
                    >
                        <b-radio-button
                            v-model="fuel.unit"
                            :native-value="canonical_consumption_unit(getHeatingFuel)"
                            size="is-medium"
                        >
                            <span>
                                {{
                                    $t(
                                        `unit.${canonical_consumption_unit(
                                            getHeatingFuel
                                        )}/year`
                                    )
                                }}
                            </span>
                        </b-radio-button>
                        <b-radio-button
                            v-model="fuel.unit"
                            native-value="kwh"
                            size="is-medium"
                        >
                            <span>{{ $t('unit.kwh/year') }}</span>
                        </b-radio-button>
                    </div>

                    <b-field
                        v-if="!getIsFuelElectricity"
                        class="section-inputs-electricity"
                        :label="$t('value.electricity')"
                        :type="{
                            'is-danger': !$v.electricity.amount.numeric,
                        }"
                        :message="[
                            !$v.electricity.amount.numeric
                                ? $t('validation.only_numbers')
                                : null,
                        ]"
                    >
                        <b-input
                            v-model="electricity.amount"
                            size="is-medium"
                            class="ers-input-field"
                        />
                        <p class="unit">{{ $t('unit.kwh/year') }}</p>
                    </b-field>
                </div>
            </div>

            <div
                v-if="!getIsFuelElectricity && !getFeatures.energy_consumption_known"
                class="ers-page-section"
            >
                <div class="ers-page-title">
                    <h2>
                        {{ $t('energy.consumers.question_1b') }}
                        <Tooltip
                            class="ers-page-title-tooltip"
                            :html="
                                $t(
                                    'energy.consumers.tooltip_popup_1',
                                    getFuelConsumptionData
                                )
                            "
                        />
                    </h2>
                </div>
                <multiple-choice v-model="fuel.estimate" :answers="levels()" center />
            </div>

            <div
                v-if="
                    (getIsFuelElectricity || fuel.estimate) &&
                        !getFeatures.energy_consumption_known
                "
                class="ers-page-section"
            >
                <h2>
                    {{ $t('energy.consumers.question_2b') }}
                    <Tooltip
                        class="ers-page-title-tooltip"
                        :html="
                            $t('energy.consumers.tooltip_popup_2', getElecConsumptionData)
                        "
                    />
                </h2>
                <multiple-choice
                    v-model="electricity.estimate"
                    :answers="levels()"
                    center
                />
            </div>
        </div>

        <div class="column is-12 mt-2">
            <div class="is-flex is-justify-content-space-between">
                <b-button
                    size="is-medium"
                    type="is-dark"
                    icon-left="arrow-left"
                    @click="changePage(false)"
                >
                    {{ $t('common.previous') }}
                </b-button>
                <submit-button
                    v-if="showNextButton"
                    @on-click="
                        $emit('open-summary-modal', {
                            unsubmit: false,
                        })
                        changePage(true)
                    "
                />
                <b-button
                    v-if="showNextButton"
                    :class="{ 'ers-next-bttn-dark': hasRequestBeenUnsubmitted }"
                    size="is-medium"
                    type="is-primary"
                    icon-right="arrow-right"
                    @click="changePage(true)"
                >
                    <span>{{ $t('common.next') }}</span>
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex'
import PeopleSelect from '../../elements/PeopleSelect.vue'
import MultipleChoice from '../../elements/MultipleChoice.vue'
import SubmitButton from '../../elements/SubmitButton.vue'
import { numeric } from 'vuelidate/lib/validators'
import Tooltip from '../../elements/Tooltip.vue'
import utils from '@/shared/plugins/utils'

const isPositive = (val) => +val > 0

export default {
    name: 'Consumers',
    components: {
        PeopleSelect,
        SubmitButton,
        MultipleChoice,
        Tooltip,
    },
    props: {
        proceedFn: {
            type: Function,
            required: false,
            default: null,
        },
        cancelFn: {
            type: Function,
            required: false,
            default: null,
        },
    },
    data: () => ({
        n_residents: 0,
        isFirstPass: true,
        fuel: {
            estimate: null,
            amount: null,
            unit: null,
        },
        electricity: {
            estimate: null,
            amount: null,
            unit: null,
        },
    }),
    validations: {
        fuel: {
            amount: {
                numeric,
            },
        },
        electricity: {
            amount: {
                numeric,
            },
        },
    },
    computed: {
        ...mapGetters('ersStore', [
            'getFeatures',
            'getHeatingFuel',
            'getRegion',
            'getOtherData',
            'hasRequestBeenUnsubmitted',
            'getConsumptionKnown',
        ]),
        showNextButton() {
            if (this.n_residents === 0) return false
            if (
                (['electricity'].includes(this.getHeatingFuel) && this.fuel.estimate) ||
                this.fuel.amount ||
                this.electricity.estimate
            ) {
                return true
            }

            return false
        },
        getIsFuelElectricity() {
            return this.getHeatingFuel === 'electricity'
        },
        getFuelConsumptionData() {
            const consumptionsDatas = utils.consumptions.kwh
            const heatingFuel =
                this.getHeatingFuel || this.getFeatures.heating_attributes[0].heating_type
            return {
                low_heat_cons: consumptionsDatas[this.getRegion][heatingFuel].min,
                medium_heat_cons: consumptionsDatas[this.getRegion][heatingFuel].med,
                high_heat_cons: consumptionsDatas[this.getRegion][heatingFuel].max,
            }
        },
        getElecConsumptionData() {
            const consumptionsDatas = utils.consumptions.kwh
            if (this.getIsFuelElectricity) {
                const heatingFuel =
                    this.getHeatingFuel ||
                    this.getFeatures.heating_attributes[0].heating_type
                return {
                    low_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].min +
                        consumptionsDatas[this.getRegion][heatingFuel].min,
                    medium_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].med +
                        consumptionsDatas[this.getRegion][heatingFuel].med,
                    high_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].max +
                        consumptionsDatas[this.getRegion][heatingFuel].max,
                }
            } else {
                return {
                    low_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].min,
                    medium_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].med,
                    high_elec_cons:
                        consumptionsDatas[this.getRegion]['electricity_heat'].max,
                }
            }
        },
        types() {
            let types = []

            if (['gas'].includes(this.getHeatingFuel)) {
                types.push({
                    value: 'boiler:standard',
                })
            }

            if (['gas', 'oil'].includes(this.getHeatingFuel)) {
                types.push({
                    value: 'boiler:condensing',
                })
            }

            if (['gas', 'oil', 'wood', 'pellets', 'coal'].includes(this.getHeatingFuel)) {
                types.push({
                    value: 'stove',
                })
            }

            if (['electricity'].includes(this.getHeatingFuel)) {
                types.push({
                    value: 'heat_pump',
                })
                types.push({
                    value: 'electric_heater',
                })
            }
            return types
        },
        getElectricity() {
            return Object.assign({}, this.electricity)
        },
        getFuel() {
            return Object.assign({}, this.fuel)
        },
    },
    watch: {
        getElectricity: {
            // https://stackoverflow.com/questions/62729380/vue-watch-outputs-same-oldvalue-and-newvalue
            handler(newVal, oldVal) {
                if (newVal.estimate !== oldVal.estimate && this.isFirstPass) {
                    this.changePage(true)
                } else {
                    this.isFirstPass = true
                }
            },
            deep: true,
        },
        getFuel: {
            handler(newVal, oldVal) {
                if (
                    ['electricity'].includes(this.getHeatingFuel) &&
                    newVal.estimate !== oldVal.estimate &&
                    this.isFirstPass
                ) {
                    this.changePage(true)
                } else {
                    this.isFirstPass = true
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.fuel.unit = 'kwh'

        const {
            n_residents,
            known_consumptions,
            estimated_consumptions,
        } = this.getFeatures

        if (!!n_residents) {
            this.isFirstPass = false
            this.n_residents = n_residents
        }

        if (this.getIsFuelElectricity) {
            if (!this.getConsumptionKnown) {
                if (estimated_consumptions.length) {
                    this.electricity.estimate = estimated_consumptions[0].estimate
                }
            } else {
                if (known_consumptions.length) {
                    this.fuel.amount = known_consumptions[0].amount
                }
            }
        } else {
            if (!this.getConsumptionKnown) {
                estimated_consumptions.map((cons) => {
                    const estimate = cons.estimate
                    const fuelType = cons.consumption_fuel
                    if (fuelType === 'electricity') {
                        this.electricity.estimate = estimate
                    } else {
                        this.fuel.estimate = estimate
                    }
                })
            } else {
                known_consumptions.map((cons) => {
                    const amount = cons.amount
                    const fuelType = cons.consumption_fuel
                    if (fuelType === 'electricity') {
                        this.electricity.amount = amount
                    } else {
                        this.fuel.amount = amount
                        this.fuel.unit = cons.unit
                    }
                })
            }
        }
    },
    methods: {
        ...mapMutations('ersStore', ['SET_FEATURES']),
        ...mapMutations('ersStore', ['UPDATE_STATUS']),
        ...mapMutations('ersStore', ['SET_CONSUMPTION']),
        ...mapActions('ersStore', ['submit_features', 'set_and_submit_feature']),

        levels() {
            return [
                {
                    name: this.$t('value.low'),
                    value: 'low',
                },
                {
                    name: this.$t('value.medium'),
                    value: 'medium',
                },
                {
                    name: this.$t('value.high'),
                    value: 'high',
                },
            ]
        },
        canonical_consumption_unit(fuel) {
            switch (fuel) {
                case 'gas':
                    return 'm3'
                case 'oil':
                    return 'l'
                case 'wood':
                case 'pellets':
                case 'coal':
                    return 'kg'
                case 'district_heating':
                    return 'kwh'
                case 'electricity':
                    return 'kwh'
                default:
                    return ''
            }
        },
        changePage(isNextPage) {
            const nextPage = isNextPage ? 'ers.big-consumers' : 'ers.energy-consumption'
            if (this.getFeatures.energy_consumption_known) {
                this.SET_CONSUMPTION({
                    consumption: {
                        consumption_fuel: this.getHeatingFuel,
                        amount: +this.fuel.amount,
                        unit: this.fuel.unit || 'kwh',
                    },
                    consumption_known: true,
                })
                this.submit_features(nextPage)
                if (!['electricity'].includes(this.getHeatingFuel)) {
                    this.SET_CONSUMPTION({
                        consumption: {
                            consumption_fuel: 'electricity',
                            amount: +this.electricity.amount,
                            unit: this.canonical_consumption_unit('electricity'),
                        },
                        consumption_known: true,
                    })
                    this.submit_features(nextPage)
                }
            } else {
                if (this.getIsFuelElectricity) {
                    this.SET_CONSUMPTION({
                        consumption: {
                            consumption_fuel: 'electricity',
                            estimate: this.electricity.estimate,
                        },
                        consumption_known: false,
                    })
                    this.submit_features(nextPage)
                } else {
                    this.SET_CONSUMPTION({
                        consumption: {
                            consumption_fuel: this.getHeatingFuel,
                            estimate: this.fuel.estimate,
                        },
                        consumption_known: false,
                    })
                    this.SET_CONSUMPTION({
                        consumption: {
                            consumption_fuel: 'electricity',
                            estimate: this.electricity.estimate,
                        },
                        consumption_known: false,
                    })
                    this.submit_features(nextPage)
                }
            }

            const feature = {
                n_residents: this.n_residents,
            }

            this.set_and_submit_feature({
                feature,
                nextPage,
            })

            this.$router.push({
                name: nextPage,
                params: {
                    lang: this.$route.params.lang,
                },
                query: this.$route.query,
            })
        },
    },
}
</script>

<style lang="scss">
h2 {
    color: #001837;
    font-family: Satoshi;
    font-size: 1.25rem;
    font-style: normal;
    font-weight: 700 !important;
    line-height: normal;
}

.ers-page-title {
    display: flex;
}

.ers-page-title-tooltip {
    .ers-tooltip-icon {
        position: relative;
        transform: translateY(28%);
        margin-inline-start: 0.64rem;
    }
}

.unit {
    display: grid;
    align-items: center;
    transform: translateX(-150%);
    z-index: 4;
}

.section-inputs {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 2rem;
    min-width: 100%;
    &-item {
        flex-grow: 1;
    }
}

.section-radios {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0rem;
    margin-block-start: auto;
    margin-bottom: 1rem;
    margin-inline-start: auto;
}

.ers-page-consumption {
    display: flex;
    flex-direction: column;
    gap: 3rem;
}

.section-inputs-electricity {
    min-width: 100%;
}
</style>
